import {
  AdornedInput,
  Alert,
  Box,
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  IconButton,
  InlineFeedback,
  Input,
  Label,
  LabeledControl,
  Paragraph,
  Text,
  TextButton,
  ToggleButton,
} from '@zillow/constellation-10';
import { IconExternalOutline, IconMinusFilled, IconPlusFilled } from '@zillow/constellation-icons';
import { formatCurrencyInputValue } from '../../../../utils/formatCurrencyInputValue';
import { stripNonDigits } from '../../../../utils/stripNonDigits';
import type { FormState } from '../formState';
import {
  ASSISTANCE_ANIMALS_URL,
  PET_COUNT_MAX,
  PET_COUNT_MIN,
  PET_SECTION_INTRO,
  PET_TYPE_OPTIONS,
  PET_TYPE_REQUIRED_MESSAGE,
} from '../constants';

type PetCountKey = 'petCatCount' | 'petSmallDogCount' | 'petLargeDogCount';

type PetPolicySectionProps = {
  formState: FormState;
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  onPetCountChange: (key: PetCountKey, delta: number) => void;
};

export default function PetPolicySection({ formState, updateForm, onPetCountChange }: PetPolicySectionProps) {
  const catCount = parseInt(formState.petCatCount, 10) || 0;
  const smallDogCount = parseInt(formState.petSmallDogCount, 10) || 0;
  const largeDogCount = parseInt(formState.petLargeDogCount, 10) || 0;
  const hasAtLeastOnePetType = catCount > 0 || smallDogCount > 0 || largeDogCount > 0;
  const showPetTypeRequiredMessage = formState.isPetsAllowed && !hasAtLeastOnePetType;

  return (
    <Box>
      <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'tight' }}>
        Pets policy
      </Heading>
      <Paragraph textStyle="body" css={{ marginBottom: 'default' }}>
        {PET_SECTION_INTRO}
      </Paragraph>
      <FieldSet>
        <Label css={{ display: 'block', marginBottom: 'default' }}>Are pets allowed?</Label>
        <Box
          role="group"
          aria-label="Are pets allowed?"
          css={{
            display: 'flex',
            width: '100%',
            marginBottom: 'default',
            gap: 'tight',
            '& > *': { flex: 1 },
          }}
        >
          <ToggleButton selected={formState.isPetsAllowed === true} onClick={() => updateForm('isPetsAllowed', true)}>
            Yes
          </ToggleButton>
          <ToggleButton selected={formState.isPetsAllowed === false} onClick={() => updateForm('isPetsAllowed', false)}>
            No
          </ToggleButton>
        </Box>
        {formState.isPetsAllowed === false && (
          <Alert tone="info" css={{ marginBottom: 'default' }}>
            You aren&apos;t required to allow pets, but may still be asked to allow assistance animals
          </Alert>
        )}
      </FieldSet>

      {formState.isPetsAllowed === false && (
        <LabeledControl
          label={<Label>Pet policy is negotiable</Label>}
          control={
            <Checkbox
              checked={formState.isPetPolicyNegotiable}
              onChange={(e) => updateForm('isPetPolicyNegotiable', e.target.checked)}
            />
          }
          css={{ marginTop: 'default' }}
        />
      )}

      {formState.isPetsAllowed && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', marginTop: 'default' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text textStyle="body-sm-bold">Pet type</Text>
            <Text textStyle="body-sm-bold" css={{ marginRight: 'layout.loose' }}>
              Maximum
            </Text>
          </Box>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
            {PET_TYPE_OPTIONS.map(({ label, key }) => (
              <Box
                key={key}
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Text textStyle="body">{label}</Text>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                  <IconButton
                    size="sm"
                    shape="circle"
                    title="Decrease pet count"
                    tone="neutral"
                    emphasis="bare"
                    aria-label="Decrease"
                    icon={<IconMinusFilled />}
                    onClick={() => onPetCountChange(key, -1)}
                    disabled={parseInt(formState[key], 10) <= PET_COUNT_MIN}
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formState[key]}
                    onChange={(e) => {
                      const value = stripNonDigits(e.target.value);
                      const num =
                        value === ''
                          ? PET_COUNT_MIN
                          : Math.min(PET_COUNT_MAX, Math.max(PET_COUNT_MIN, parseInt(value, 10)));
                      updateForm(key, String(num));
                    }}
                    css={{
                      paddingInline: 'default',
                      paddingBlock: 'tighter',
                      width: '56px',
                      textAlign: 'center',
                    }}
                  />
                  <IconButton
                    size="sm"
                    shape="circle"
                    title="Increase pet count"
                    tone="neutral"
                    emphasis="bare"
                    aria-label="Increase"
                    icon={<IconPlusFilled />}
                    onClick={() => onPetCountChange(key, 1)}
                    disabled={parseInt(formState[key], 10) >= PET_COUNT_MAX}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          {showPetTypeRequiredMessage && (
            <InlineFeedback tone="critical" css={{ marginBottom: 'default' }}>
              {PET_TYPE_REQUIRED_MESSAGE}
            </InlineFeedback>
          )}

          <LabeledControl
            label={<Label>Pet policy is negotiable</Label>}
            control={
              <Checkbox
                checked={formState.isPetPolicyNegotiable}
                onChange={(e) => updateForm('isPetPolicyNegotiable', e.target.checked)}
              />
            }
          />

          <Label css={{ display: 'block' }}>Do you have any pet fees?</Label>
          <FormField
            label={<Label>Pet deposit</Label>}
            control={
              <AdornedInput
                input={
                  <AdornedInput.Input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatCurrencyInputValue(formState.petDeposit)}
                    onChange={(e) => updateForm('petDeposit', stripNonDigits(e.target.value).slice(0, 6))}
                  />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
              />
            }
            description={<FormHelp>Set to $0 if you will not collect a deposit</FormHelp>}
          />
          <FormField
            label={<Label>Pet rent</Label>}
            control={
              <AdornedInput
                input={
                  <AdornedInput.Input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatCurrencyInputValue(formState.petRent)}
                    onChange={(e) => updateForm('petRent', stripNonDigits(e.target.value).slice(0, 5))}
                  />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
              />
            }
            description={<FormHelp>Set to $0 if you will not collect pet rent</FormHelp>}
          />
        </Box>
      )}

      <TextButton
        asChild
        textStyle="body-sm"
        icon={<IconExternalOutline />}
        iconPosition="end"
        css={{ marginTop: 'default' }}
      >
        <a href={ASSISTANCE_ANIMALS_URL} target="_blank" rel="noreferrer">
          Learn more about assistance animal exemptions
        </a>
      </TextButton>
    </Box>
  );
}
